import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import Select, { components, StylesConfig, OptionProps } from 'react-select';
import { ReactComponent as CaretIcon } from './svgs/caretIcon.svg';
import { ReactComponent as CheckIcon } from './svgs/checkIcon.svg';
import { ReactComponent as SearchIcon } from './svgs/searchIcon.svg';
import { Countries } from './constants/countries';
import './phoneInput.scss';

interface ISelectOptionProps {
  value: string;
  label: string;
  dialCode: number;
}

interface IDropdownProps {
  onChange: (number: string, countryCode?: any) => void;
  label?: string;
  initialValue?: string;
  searchLabel?: string;
  innerRef?: any;
  invalid?: boolean;
  errorMsg: string;
}

const selectOptions = Countries.map((country) => ({
  value: country.country_code,
  label: country.name,
  dialCode: country.dial_code,
}));

const Option = (props: OptionProps<any, any>) => {
  const { data } = props;

  return (
    <div>
      <components.Option {...props}>
        <span className={`m-r-8 flag-icon flag-icon-${data.value.toLowerCase()}`} />
        <span className="f-w-600 m-r-7 text-ellipsis">{data.label}</span>
        {` +${data.dialCode}`}
      </components.Option>
    </div>
  );
};

interface IDropdownWrapperProps {
  isOpen: boolean;
  target: React.ReactNode;
  onClose: () => void;
}

const DropdownWrapper: FunctionComponent<IDropdownWrapperProps> = ({
  children,
  isOpen,
  target,
  onClose,
}) => (
  <div className="dropdownWrapper">
    {target}
    {isOpen
      ? (
        <>
          <div className="codes-dropdown-menu">
            {children}
          </div>
          <div className="codes-dropdown-blanket" onClick={onClose} />
        </>
      )
      : null }
  </div>
);

const DropdownIndicator = () => (
  <div className="dropdown-indicator">
    <SearchIcon />
  </div>
);

const styles: StylesConfig<any, false> = {
  control: (css) => ({
    ...css,
    height: '48px',
    padding: '0 16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: 'none',
    border: '1px solid #AFB6BE',
  }),
  indicatorsContainer: (css) => ({
    ...css,
    display: 'flex',
    order: -1,
  }),
  container: (css) => ({
    ...css,
    position: 'relative',
  }),
  menu: (css) => ({
    ...css,
    width: '100%',
    boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)',
    marginTop: 8,
  }),
  menuList: (css) => ({
    ...css,
    maxHeight: 200,
    paddingTop: 0,
    borderTop: '2px solid white',
  }),
  option: (css, state) => ({
    ...css,
    display: 'flex',
    alignItems: 'center',
    color: '#38414a',
    backgroundColor: state.isFocused || state.isSelected ? '#e6f4f8' : '#fff',
    '&:active': {
      backgroundColor: '#e6f4f8',
    },
  }),
};

export const PhoneInput: FunctionComponent<IDropdownProps> = ({
  onChange,
  initialValue,
  label = 'Your mobile number',
  searchLabel = 'Search...',
  innerRef,
  invalid,
  errorMsg,
}) => {
  const splittedPhoneValues = initialValue?.split(' ');
  let initialOption;

  if (initialValue && splittedPhoneValues && splittedPhoneValues[0]) {
    initialOption = selectOptions.find(
      (country) => country.dialCode === Number(splittedPhoneValues[0]),
    ) || selectOptions[149];
  } else {
    initialOption = selectOptions[149];
  }

  const [phoneInput, setPhoneInput] = useState(
    (splittedPhoneValues && splittedPhoneValues[1]) || '',
  );
  const [successInput, setSuccessInput] = React.useState(false);
  const [prefix, setPrefix] = React.useState<ISelectOptionProps>({
    value: initialOption.value,
    label: `+${initialOption.dialCode}`,
    dialCode: initialOption.dialCode,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [focus, setFocus] = useState(false);

  const handlePrefix = (selectOption: ISelectOptionProps) => {
    setIsOpen(!isOpen);
    setPrefix({
      value: selectOption.value,
      label: `+${selectOption.dialCode}`,
      dialCode: selectOption.dialCode,
    });
  };

  useEffect(() => {
    if (!prefix?.dialCode || !phoneInput || phoneInput.length < 6) {
      setSuccessInput(false);
    } else {
      setSuccessInput(true);
    }
    const phoneNumber = `+${prefix.dialCode} ${phoneInput?.replace(/ /g, '')}`;
    if (onChange) {
      onChange(phoneNumber, prefix?.value);
    }
  }, [prefix, phoneInput]);
  return (
    <>
      <div
        className={`custom-dropdown-phone-input d-flex ${successInput ? 'success' : ''
        } ${invalid ? 'invalid' : ''} ${focus ? 'focus' : ''} ${isOpen ? 'focus' : ''}`}
      >
        <div className="label">
          {label}
        </div>

        <div className="custom-dropdown-countries">
          <DropdownWrapper
            isOpen={isOpen}
            onClose={() => setIsOpen(!isOpen)}
            target={(
              <div className="targetDiv" onClick={() => setIsOpen(!isOpen)}>
                <span className={`flag-icon flag-icon-${prefix.value.toLowerCase()}`}/>
                <span data-testid="input_dial-code" className="f-w-600">{`+${prefix.dialCode}`}</span>
                <CaretIcon direction="bottom" width={7} />
              </div>
            )}
          >
            <Select
              components={{
                Option,
                DropdownIndicator,
                IndicatorSeparator: null,
              }}
              onChange={handlePrefix}
              options={selectOptions}
              styles={styles}
              className="select-dropdown f-s-16"
              value={prefix}
              isMulti={false}
              isSearchable
              autoFocus
              backspaceRemovesValue={false}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              isClearable={false}
              menuIsOpen
              placeholder={searchLabel}
              tabSelectsValue={false}
              filterOption={(candidate: any, input: string) => {
                if (input[0] === '+') {
                  input = input.substring(1);
                }
                if (!Number.isNaN(Number(input[0]))) {
                  return candidate.data.dialCode.toString().includes(input);
                }
                return candidate.label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </DropdownWrapper>
        </div>

        <input
          id="bookerDetails.phone"
          name="bookerDetails.phone"
          type="number"
          className="phone-number-input"
          onChange={(e) => {
            setPhoneInput(e?.currentTarget?.value);
          }}
          defaultValue={phoneInput}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          ref={innerRef}
          data-testid="input_phone-number"
        />
        {successInput && <CheckIcon className="input-status-icon" />}
      </div>
      {invalid && <div className="input-error">{errorMsg}</div>}
    </>
  );
};

